import React from 'react';
import type { ItemStatusCode } from '@/features/cart/types';
import { ItemStatusCode as ItemStatusCodeConst } from '@/features/cart/types';

interface CartItemStatusWarningsProps {
  statusCode: ItemStatusCode | undefined;
  statusLoading: boolean;
  isUpdating: boolean;
  isRemoving: boolean;
  onConfirmPrice: () => void;
  onRemoveItem: () => void;
  onStartEdit: () => void;
  isTableBooking?: boolean;
}

const CartItemStatusWarnings: React.FC<CartItemStatusWarningsProps> = ({
  statusCode,
  statusLoading,
  isUpdating,
  isRemoving,
  onConfirmPrice,
  onRemoveItem,
  onStartEdit,
  isTableBooking,
}) => {
  if (statusLoading) {
    return (
      <div className="mb-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
        <div className="flex items-center gap-2 text-gray-400">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
          <span className="text-sm">Checking available spots...</span>
        </div>
      </div>
    );
  }

  if (!statusCode) return null;

  // Fatal errors that require removal
  const isFatalError = 
    statusCode === ItemStatusCodeConst.EBCCancelled ||
    statusCode === ItemStatusCodeConst.EBCInPast ||
    statusCode === ItemStatusCodeConst.EBCUnavailable ||
    statusCode === ItemStatusCodeConst.EBCSoldOut ||
    statusCode === ItemStatusCodeConst.TableGone ||
    statusCode === ItemStatusCodeConst.CurrencyMismatch;

  return (
    <div className="mb-4 space-y-2">
      {/* Price Update - Actionable */}
      {statusCode === ItemStatusCodeConst.PriceUpdateRequired && (
        <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-yellow-400 font-medium mb-1">Price Changed</p>
              <p className="text-gray-300 text-sm">The price has been updated. Please confirm to proceed.</p>
            </div>
            <button
              onClick={onConfirmPrice}
              disabled={isUpdating}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 text-sm whitespace-nowrap"
            >
              {isUpdating ? 'Confirming...' : 'Confirm Price'}
            </button>
          </div>
        </div>
      )}

      {/* Fatal Errors - Must Remove */}
      {isFatalError && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-red-400 font-medium mb-1">
                {statusCode === ItemStatusCodeConst.EBCCancelled && 'Event Cancelled'}
                {statusCode === ItemStatusCodeConst.EBCInPast && 'Event Expired'}
                {statusCode === ItemStatusCodeConst.EBCUnavailable && 'Tickets Unavailable'}
                {statusCode === ItemStatusCodeConst.EBCSoldOut && 'Sold Out'}
                {statusCode === ItemStatusCodeConst.TableGone && 'Table Unavailable'}
                {statusCode === ItemStatusCodeConst.CurrencyMismatch && 'Currency Conflict'}
              </p>
              <p className="text-gray-300 text-sm">
                {statusCode === ItemStatusCodeConst.EBCCancelled && 'This event has been cancelled. Please remove from cart.'}
                {statusCode === ItemStatusCodeConst.EBCInPast && 'This event has already happened. Please remove from cart.'}
                {statusCode === ItemStatusCodeConst.EBCUnavailable && 'These tickets are no longer available. Please remove from cart.'}
                {statusCode === ItemStatusCodeConst.EBCSoldOut && 'This ticket tier is sold out. Please remove from cart.'}
                {statusCode === ItemStatusCodeConst.TableGone && 'This table is no longer available. Please remove from cart.'}
                {statusCode === ItemStatusCodeConst.CurrencyMismatch && 'This item uses a different currency. Please remove to proceed.'}
              </p>
            </div>
            <button
              onClick={onRemoveItem}
              disabled={isRemoving}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm whitespace-nowrap"
            >
              {isRemoving ? 'Removing...' : 'Remove'}
            </button>
          </div>
        </div>
      )}

      {/* Insufficient Capacity - Actionable (Edit or Remove) */}
      {(statusCode === ItemStatusCodeConst.EBCInsufficientCapacity || 
        (statusCode === ItemStatusCodeConst.TableInsufficientCapacity && isTableBooking)) && (
        <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <p className="text-yellow-400 font-medium mb-1">
            {isTableBooking ? 'Table Too Small' : 'Not Enough Spots'}
          </p>
          <p className="text-gray-300 text-sm mb-3">
            {isTableBooking 
              ? "This table can't fit your group size. Please reduce guests or remove item."
              : "There aren't enough tickets left for your group size. Please reduce guests or remove item."
            }
          </p>
          <div className="flex gap-2">
            <button
              onClick={onStartEdit}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm"
            >
              Edit Guests
            </button>
            <button
              onClick={onRemoveItem}
              disabled={isRemoving}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
            >
              {isRemoving ? 'Removing...' : 'Remove'}
            </button>
          </div>
        </div>
      )}

      {/* Add-on Issues - Only show if NO other fatal/capacity issues */}
      {statusCode === ItemStatusCodeConst.GoodButAddOnIssues && !isFatalError && (
        <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <p className="text-yellow-400 font-medium mb-1">Add-on Issues</p>
          <p className="text-gray-300 text-sm">Some add-ons are unavailable or sold out. Please review your add-ons.</p>
        </div>
      )}
    </div>
  );
};

export default CartItemStatusWarnings;

